import { Box, Divider, Flex, Input, InputProps, Text } from '@modusign/design-system';
import { useCombobox } from 'downshift';
import { Fragment, MouseEvent as ReactMouseEvent, useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import OutsideClickHandler from 'react-outside-click-handler';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';

import useContacts from '@app/features/addressBook/hooks/useContacts';
import Contact from '@app/features/addressBook/types/Contact';
import { getHasAddressBookPermission } from '@app/store/user/authority/selectors';

import AddressBookSearchInputHelpButton from './AddressBookSearchInputHelpButton';
import AddressBookSearchInputListItem from './AddressBookSearchInputListItem';
import AddressBookSearchStatusMessage from './AddressBookSearchStatusMessage';
import InputClearButton from './InputClearButton';
import InputSearchButton from './InputSearchButton';

export interface AddressBookSearchInputProps
  extends Omit<InputProps, 'defaultValue' | 'value' | 'onChange'> {
  initialValue?: string;
  searchTarget?: Array<'name' | 'companyName'>;
  onChange: (value: string) => void;
  onSelected?: (item: Contact) => void;
}

interface ContactWithSearchKeyword extends Contact {
  searchKeyword: string;
}

const CONTACTS_FETCH_LIMIT = 10;

export default function AddressBookSearchInput({
  autoFocus,
  id,
  initialValue,
  searchTarget = ['companyName', 'name'],
  onChange,
  onSelected,
  width = '100%',
  ...props
}: AddressBookSearchInputProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<{
    contacts: ContactWithSearchKeyword[];
    count: number;
  } | null>(null);
  const [page, setPage] = useState(1);
  const [fetchEnabled, setFetchEnabled] = useState(false);
  const [isSearchHelpTooltipOpen, toggleSearchHelpTooltipOpen] = useToggle(false);

  const hasAddressBookPermission = useSelector(getHasAddressBookPermission);

  const handleInputValueChange = useCallback(
    ({ inputValue }) => {
      onChange(inputValue);
    },
    [onChange]
  );

  const {
    getComboboxProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
    inputValue,
    setInputValue,
  } = useCombobox({
    initialInputValue: initialValue,
    items: suggestions?.contacts ?? [],
    itemToString: (item) => {
      if (!item) {
        return '';
      }
      onSelected?.(item);
      setInternalIsOpen(false);
      return getTargetInputValue(item);
    },
    isOpen: internalIsOpen && hasAddressBookPermission,
    onInputValueChange: handleInputValueChange,
  });

  const getTargetInputValue = useCallback(
    (contactWithSearchKeyword: ContactWithSearchKeyword) => {
      if (
        searchTarget?.length === 1 &&
        searchTarget.includes('companyName') &&
        contactWithSearchKeyword.companyName
      ) {
        return contactWithSearchKeyword.companyName;
      }

      return contactWithSearchKeyword.name;
    },
    [searchTarget]
  );

  const { data, refetch, status } = useContacts(
    {
      companyName: searchTarget?.includes('companyName') ? inputValue : undefined,
      limit: CONTACTS_FETCH_LIMIT,
      name: searchTarget?.includes('name') ? inputValue : undefined,
      offset: (page - 1) * CONTACTS_FETCH_LIMIT,
    },
    {
      onSuccess: ({ contacts, count }) => {
        const contactsWithSearchKeyword = contacts.map((contact) => ({
          ...contact,
          searchKeyword: inputValue,
        }));
        setSuggestions((prev) => ({
          contacts: prev
            ? prev.contacts.concat(contactsWithSearchKeyword)
            : contactsWithSearchKeyword,
          count,
        }));
      },
      onSettled: () => setFetchEnabled(false),
      enabled: fetchEnabled && hasAddressBookPermission && !!inputValue,
    }
  );

  const maxPage = (data?.count && Math.ceil(data.count / CONTACTS_FETCH_LIMIT)) ?? 1;
  const hasMore = page < maxPage;

  const handleCloseSuggestions = useCallback(() => {
    setInternalIsOpen(false);
    setSuggestions(null);
    setPage(1);
  }, []);

  const handleClear = useCallback(() => {
    handleCloseSuggestions();
    setFetchEnabled(false);
    setInputValue('');
  }, [handleCloseSuggestions, setInputValue]);

  const handleSearch = useCallback(() => {
    if (inputValue?.trim()) {
      setFetchEnabled(true);
      setInternalIsOpen(true);
      setSuggestions(null);
      setPage(1);
    }
  }, [inputValue]);

  const isSuggestionOpen = internalIsOpen && (suggestions || status === 'error');

  const handleFetchMore = useCallback(() => {
    setPage((prev) => prev + 1);
    setFetchEnabled(true);
  }, []);

  useEffect(() => {
    setInputValue(initialValue ?? '');
  }, [initialValue, setInputValue]);

  return (
    <Box display="inline-block" position="relative" width={width}>
      <OutsideClickHandler
        onOutsideClick={() => {
          setInternalIsOpen(false);
        }}
      >
        <div {...getComboboxProps()}>
          <Box position="relative" width="100%">
            <Input
              {...props}
              {...getInputProps({
                autoFocus,
                id,
                onBlur: () => {
                  setInputValue(inputValue?.trim());
                },
              })}
              sx={{ paddingRight: hasAddressBookPermission ? '72px' : '36px' }}
              onKeyDown={(event) => {
                toggleSearchHelpTooltipOpen(false);
                if (event.key === 'Enter') {
                  // 엔터키로 form submit 이벤트 전파 방지
                  event.preventDefault();
                  event.stopPropagation();

                  handleSearch();
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  event.stopPropagation();

                  handleCloseSuggestions();
                }
              }}
            />
            <Flex
              height="100%"
              justifyContent="flex-end"
              mr="1px"
              mt="1px"
              position="absolute"
              right={0}
              top={0}
            >
              {inputValue && <InputClearButton onClick={handleClear} />}
              {inputValue && hasAddressBookPermission && (
                <InputSearchButton
                  disabled={!inputValue}
                  isLoading={status === 'loading'}
                  onClick={handleSearch}
                />
              )}
            </Flex>
          </Box>
          <Box
            as="ul"
            backgroundColor="white"
            borderRadius="3px"
            boxShadow={'0 2px 12px rgba(0, 0, 0, 0.1)'}
            display={isSuggestionOpen ? 'block' : 'none'}
            fontSize="90%"
            left={0}
            opacity={1}
            overflow="auto"
            p={0}
            position="absolute"
            top="36px"
            width="100%"
            zIndex={100}
            {...getMenuProps()}
          >
            <Flex alignItems="center" justifyContent="space-between" maxHeight="210px" p={4}>
              <Flex alignItems="center">
                <Text as="p" color="textNeutralVeryLight" fontSize="sm">
                  주소록 검색 결과
                </Text>
                {suggestions && (
                  <Text
                    as="p"
                    bg="statusBackgroundEmphasisUltraLight"
                    color="textEmphasisBase"
                    data-testid="address-book-search-input-fetch-result-count"
                    fontSize="xxs"
                    fontWeight="medium"
                    ml={2}
                    mt="2px"
                    pt="2px"
                    px={2}
                    sx={{ height: '16px', borderRadius: '10px' }}
                    textAlign="center"
                  >
                    {suggestions.count}
                  </Text>
                )}
              </Flex>
              <AddressBookSearchInputHelpButton
                isTooltipOpen={isSearchHelpTooltipOpen}
                onClick={toggleSearchHelpTooltipOpen}
              />
            </Flex>
            <Box data-testid="address-book-search-input-scroll-box" height="168px" overflow="auto">
              <InfiniteScroll
                hasMore={hasMore}
                loadMore={handleFetchMore}
                pageStart={0}
                threshold={10}
                useWindow={false}
              >
                {suggestions?.contacts.map((item, index) => (
                  <Fragment key={item.id}>
                    <Box
                      as="li"
                      bg={
                        highlightedIndex === index ? 'formBackgroundNeutralSlightlyDark' : undefined
                      }
                      cursor="pointer"
                      key={item.id}
                      p={4}
                      {...getItemProps({
                        item,
                        index,
                        onMouseDown: (e: ReactMouseEvent<HTMLLIElement, MouseEvent>) => {
                          e.stopPropagation();
                        },
                      })}
                    >
                      <AddressBookSearchInputListItem
                        companyName={item.companyName}
                        email={item.email}
                        name={item.name}
                        phoneNumber={item.phoneNumber}
                      />
                    </Box>
                    <Divider color="light" />
                  </Fragment>
                ))}
                <AddressBookSearchStatusMessage
                  count={suggestions?.contacts.length ?? 0}
                  hasMore={page < maxPage}
                  searchKeyword={inputValue}
                  status={status}
                  totalCount={suggestions?.count ?? 0}
                  onFetchMore={() => {
                    setPage((prev) => prev + 1);
                    setFetchEnabled(true);
                  }}
                  onRetry={() => refetch()}
                />
              </InfiniteScroll>
            </Box>
          </Box>
        </div>
      </OutsideClickHandler>
    </Box>
  );
}
